import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const NavigationContext = createContext();

const STORAGE_KEY = "nav-active-panel"; // "", "before", "during", "after"

// Helpers for hash-based panel state (e.g. #before-quick-tips)
function setHashPanel(panel) {
  if (typeof window === "undefined") return;
  window.location.hash = panel ? panel : "";
}

function getHashPanel() {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash || "";
  const value = raw.replace(/^#/, ""); // e.g. "before-quick-tips"
  return value || null;
}

// Map During hub index → hash fragment
function getDuringHashForIndex(idx) {
  switch (idx) {
    case 0:
      return "during-updates";
    case 1:
      return "during-weather";
    case 2:
      return "during-transportation";
    case 3:
      return "during-utilities";
    case 4:
      return "during-aid";
    default:
      return "during";
  }
}

export function NavigationProvider({ children }) {
  const [roadmapOpen, setRoadmapOpen] = useState(false);
  const [checklistsOpen, setChecklistsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(""); // "before" | "during" | "after" | ""
  const [sitRepPanelIndex, setSitRepPanelIndex] = useState(0);
  const [openHub, setOpenHub] = useState(false);
  const [openTimeline, setTimelineOpen] = useState(false);
  const [openHubFlag, setOpenHubFlag] = useState(false);

  // Global weather alert sidebar state
  const [weatherAlertSidebarOpen, setWeatherAlertSidebarOpen] =
    useState(false);
  const [weatherAlertForSidebar, setWeatherAlertForSidebar] =
    useState(null);

  // Before highlights
  const [highlightChecklist, setHighlightChecklist] = useState(false);
  const [highlightRoadmap, setHighlightRoadmap] = useState(false);
  const [highlightQuickTips, setHighlightQuickTips] = useState(false);
  const [highlightSupplyLists, setHighlightSupplyLists] = useState(false);

  // "During" hub nav highlight
  const [highlightHubNavIndex, setHighlightHubNavIndex] =
    useState(null);

  // Before scroll/centering
  const [roadmapCenterRequested, setRoadmapCenterRequested] =
    useState(false);
  const [beforeScrollRequested, setBeforeScrollRequested] =
    useState(false);
  const [quickTipsCenterRequested, setQuickTipsCenterRequested] =
    useState(false);
  const [lastBeforeTarget, setLastBeforeTarget] = useState(null);

  // During scroll
  const [duringScrollRequested, setDuringScrollRequested] =
    useState(false);
  const [duringSubIndex, setDuringSubIndex] = useState(0);

  // After-specific state
  const [highlightAfterTimeline, setHighlightAfterTimeline] =
    useState(false);
  const [highlightAfterTips, setHighlightAfterTips] =
    useState(false);
  const [highlightAfterChecklists, setHighlightAfterChecklists] =
    useState(false);
  const [lastAfterTarget, setLastAfterTarget] = useState(null);
  const [afterTargetPanelId, setAfterTargetPanelId] = useState(null);
  const [afterScrollRequested, setAfterScrollRequested] =
    useState(false);

  const trackNavigation = useCallback((eventName, params = {}) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, {
        event_category: "Navigation",
        ...params,
      });
    }
    if (process.env.NODE_ENV === "development") {
      console.log("GA event:", eventName, params);
    }
  }, []);

  // Close all three primary panels in a consistent way
  const closeAllPrimaryPanels = useCallback(() => {
    setRoadmapOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);
    setActivePanel("");
  }, []);

  // Open exactly one primary panel (Before/During/After) at a time
  const openExclusive = useCallback((panelKey) => {
    if (panelKey === "before") {
      setRoadmapOpen(true);
      setOpenHub(false);
      setTimelineOpen(false);
    } else if (panelKey === "during") {
      setRoadmapOpen(false);
      setOpenHub(true);
      setTimelineOpen(false);
    } else if (panelKey === "after") {
      setRoadmapOpen(false);
      setOpenHub(false);
      setTimelineOpen(true);
    } else {
      setRoadmapOpen(false);
      setOpenHub(false);
      setTimelineOpen(false);
    }
    setActivePanel(panelKey || "");
  }, []);

  // BEFORE openers

  // Top-level Before, treated as Quick Tips by default
  const openBeforePlain = useCallback(
    (source = "unknown") => {
      openExclusive("before");

      // Scroll is handled in IndexPage via beforeScrollRequested
      setBeforeScrollRequested(true);

      // Only rewrite hash when not hash/deep-link driven
      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before-quick-tips"); // canonical hash for Before default
      }

      trackNavigation("open_before_plain", { source });
    },
    [openExclusive, trackNavigation]
  );

  // Readiness Roadmap
  const openRoadmap = useCallback(
    (source = "unknown") => {
      openExclusive("before");

      setRoadmapCenterRequested(true);
      setHighlightRoadmap(true);
      setLastBeforeTarget("readiness-roadmap");

      setBeforeScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before-roadmap");
      }

      trackNavigation("open_roadmap", { source });
    },
    [openExclusive, trackNavigation]
  );

  // Quick Safety Tips and Tools
  const openBeforeGeneric = useCallback(
    (source = "unknown") => {
      openExclusive("before");

      setLastBeforeTarget("quick-safety-tips-and-tools");
      setHighlightQuickTips(true);
      setQuickTipsCenterRequested(true);

      setBeforeScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before-quick-tips");
      }

      trackNavigation("open_before_generic", { source });
    },
    [openExclusive, trackNavigation]
  );

  // Preparedness Checklists
  const openBeforeChecklists = useCallback(
    (source = "unknown") => {
      openExclusive("before");

      setChecklistsOpen(true);
      setHighlightChecklist(true);
      setLastBeforeTarget("preparedness-checklists");

      setBeforeScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before-checklists");
      }

      trackNavigation("open_before_checklists", { source });
    },
    [openExclusive, trackNavigation]
  );

  // Supply Lists
  const openBeforeSupplyLists = useCallback(
    (source = "unknown") => {
      openExclusive("before");

      // Reuse checklistsOpen to trigger horizontal centering
      setChecklistsOpen(true);
      setHighlightSupplyLists(true);
      setLastBeforeTarget("supply-lists");

      setBeforeScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before-supply-lists");
      }

      trackNavigation("open_before_supply_lists", { source });
    },
    [openExclusive, trackNavigation]
  );

  // DURING

  const openEmergencyHub = useCallback(
    (source = "unknown", subIndex = 0) => {
      openExclusive("during");

      // tell SitRepCentral which panel is active
      setSitRepPanelIndex(subIndex);
      setDuringSubIndex(subIndex);

      setDuringScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        if (typeof window !== "undefined") {
          const current = (window.location.hash || "").replace(/^#/, "");
          const isWeatherChip = current.startsWith("during-weather-");
          if (!isWeatherChip) {
            const hash = getDuringHashForIndex(subIndex);
            setHashPanel(hash);
          }
        }
      }

      trackNavigation("open_hub", { source, subIndex });
    },
    [openExclusive, trackNavigation]
  );

  // AFTER

  // Top-level Recovery Timeline section
  const openTimelinePanel = useCallback(
    (source = "unknown") => {
      openExclusive("after");

      setLastAfterTarget("recovery-timeline");

      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("recovery-timeline");
      }, 0);

      // Always request scroll so IndexPage can apply HEADER_OFFSET
      setAfterScrollRequested(true);

      // Only rewrite hash when this wasn't hash/deep-link driven
      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("after");
      }

      trackNavigation("open_timeline", { source });
    },
    [openExclusive, trackNavigation]
  );

  // After: Mental Health
  const openAfterMentalHealth = useCallback(
    (source = "unknown") => {
      openExclusive("after");

      setHighlightAfterTips(true);
      setLastAfterTarget("mental-health-resources");

      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("mental-health-resources");
      }, 0);

      setAfterScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("after-mental-health");
      }

      trackNavigation("open_after_mental_health", { source });
    },
    [openExclusive, trackNavigation]
  );

  // After: Timeline (explicit deep link)
  const openAfterTimeline = useCallback(
    (source = "unknown") => {
      openExclusive("after");

      setHighlightAfterTimeline(true);
      setLastAfterTarget("recovery-timeline");

      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("recovery-timeline");
      }, 0);

      setAfterScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("after-timeline");
      }

      trackNavigation("open_after_timeline", { source });
    },
    [openExclusive, trackNavigation]
  );

  // After: Checklists
  const openAfterChecklists = useCallback(
    (source = "unknown") => {
      openExclusive("after");

      setHighlightAfterChecklists(true);
      setLastAfterTarget("recovery-checklists");

      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("recovery-checklists");
      }, 0);

      setAfterScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("after-checklists");
      }

      trackNavigation("open_after_checklists", { source });
    },
    [openExclusive, trackNavigation]
  );

  // WEATHER

  const openWeatherAlertSidebar = useCallback(
    (alert, source = "unknown") => {
      setWeatherAlertForSidebar(alert);
      setWeatherAlertSidebarOpen(true);
      trackNavigation("open_weather_alert_sidebar", {
        source,
        event: alert?.properties?.event,
        severity: alert?.properties?.severity,
      });
    },
    [trackNavigation]
  );

  const closeWeatherAlertSidebar = useCallback(
    (source = "unknown") => {
      setWeatherAlertSidebarOpen(false);
      trackNavigation("close_weather_alert_sidebar", { source });
    },
    [trackNavigation]
  );

  const closePanels = useCallback(() => {
    setRoadmapOpen(false);
    setChecklistsOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);
    setActivePanel("");
    setWeatherAlertSidebarOpen(false);
    setWeatherAlertForSidebar(null);
    setHighlightChecklist(false);
    setHighlightRoadmap(false);
    setHighlightQuickTips(false);
    setHighlightSupplyLists(false);
    setHighlightHubNavIndex(null);
    setRoadmapCenterRequested(false);
    setBeforeScrollRequested(false);
    setQuickTipsCenterRequested(false);
    setDuringScrollRequested(false);
    setHighlightAfterTips(false);
    setHighlightAfterTimeline(false);
    setHighlightAfterChecklists(false);
    setAfterTargetPanelId(null);
    setAfterScrollRequested(false);
    setHashPanel(null); // clear #...
  }, []);

  // Map current hash to UI state
  const applyHashPanel = useCallback(
    (source = "hashchange") => {
      const hashPanel = getHashPanel();

      // Top-level hashes (before maps to Quick Tips)
      if (hashPanel === "before") {
        openBeforeGeneric(source);
        return;
      }
      if (hashPanel === "during") {
        openEmergencyHub(source, 0);
        return;
      }
      if (hashPanel === "after") {
        openTimelinePanel(source);
        return;
      }

      // Specific Before hashes
      if (hashPanel === "before-roadmap") {
        openRoadmap(source);
        return;
      }
      if (hashPanel === "before-quick-tips") {
        openBeforeGeneric(source);
        return;
      }
      if (hashPanel === "before-checklists") {
        openBeforeChecklists(source);
        return;
      }
      if (hashPanel === "before-supply-lists") {
        openBeforeSupplyLists(source);
        return;
      }

      // Specific During hashes
      if (hashPanel === "during-hub") {
        openEmergencyHub(source, 0);
        return;
      }
      if (hashPanel === "during-updates") {
        openEmergencyHub(source, 0);
        return;
      }
      if (hashPanel === "during-weather") {
        openEmergencyHub(source, 1);
        return;
      }
      if (hashPanel === "during-transportation") {
        openEmergencyHub(source, 2);
        return;
      }
      if (hashPanel === "during-utilities") {
        openEmergencyHub(source, 3);
        return;
      }
      if (hashPanel === "during-aid") {
        openEmergencyHub(source, 4);
        return;
      }

      // During-weather chip hashes: keep hash as-is, just open weather view
      if (hashPanel && hashPanel.startsWith("during-weather-")) {
        openEmergencyHub(source, 1);
        return;
      }

      // Specific After hashes
      if (hashPanel === "after-timeline-panel") {
        openTimelinePanel(source);
        return;
      }
      if (hashPanel === "after-timeline") {
        openAfterTimeline(source);
        return;
      }
      if (hashPanel === "after-mental-health") {
        openAfterMentalHealth(source);
        return;
      }
      if (hashPanel === "after-checklists") {
        openAfterChecklists(source);
        return;
      }
    },
    [
      openBeforeGeneric,
      openEmergencyHub,
      openTimelinePanel,
      openRoadmap,
      openBeforeChecklists,
      openBeforeSupplyLists,
      openAfterTimeline,
      openAfterMentalHealth,
      openAfterChecklists,
    ]
  );

  // On first mount: honor hash deep-link, else fall back to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;

    const hashPanel = getHashPanel();

    if (hashPanel) {
      applyHashPanel("deep_link");
      return;
    }

    const saved = window.localStorage.getItem(STORAGE_KEY) || "";

    setRoadmapOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);

    setActivePanel(saved);

    if (saved === "before") {
      setRoadmapOpen(true);
    } else if (saved === "during") {
      setOpenHub(true);
    } else if (saved === "after") {
      setTimelineOpen(true);
    }
  }, [applyHashPanel]);

  // Listen for hashchange
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleHashChange = () => {
      applyHashPanel("hashchange");
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [applyHashPanel]);

  // Persist activePanel
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, activePanel);
  }, [activePanel]);

  const value = {
    roadmapOpen,
    setRoadmapOpen,
    checklistsOpen,
    setChecklistsOpen,
    activePanel,
    setActivePanel,
    sitRepPanelIndex,
    setSitRepPanelIndex,
    openHub,
    setOpenHub,
    openTimeline,
    setTimelineOpen,
    openHubFlag,
    setOpenHubFlag,

    // Before
    openBeforePlain,
    openRoadmap,
    openBeforeGeneric,
    openBeforeChecklists,
    openBeforeSupplyLists,

    // During
    openEmergencyHub,

    // Weather
    weatherAlertSidebarOpen,
    weatherAlertForSidebar,
    openWeatherAlertSidebar,
    closeWeatherAlertSidebar,

    // Before highlights
    highlightChecklist,
    setHighlightChecklist,
    highlightRoadmap,
    setHighlightRoadmap,
    highlightQuickTips,
    setHighlightQuickTips,
    highlightSupplyLists,
    setHighlightSupplyLists,

    // Hub highlights
    highlightHubNavIndex,
    setHighlightHubNavIndex,

    // Scroll / state
    closePanels,
    trackNavigation,
    roadmapCenterRequested,
    setRoadmapCenterRequested,
    beforeScrollRequested,
    setBeforeScrollRequested,
    quickTipsCenterRequested,
    setQuickTipsCenterRequested,
    lastBeforeTarget,
    setLastBeforeTarget,

    duringScrollRequested,
    setDuringScrollRequested,
    duringSubIndex,
    setDuringSubIndex,

    // After
    highlightAfterTimeline,
    setHighlightAfterTimeline,
    highlightAfterTips,
    setHighlightAfterTips,
    highlightAfterChecklists,
    setHighlightAfterChecklists,
    lastAfterTarget,
    setLastAfterTarget,
    afterTargetPanelId,
    setAfterTargetPanelId,
    openAfterMentalHealth,
    openAfterTimeline,
    openAfterChecklists,
    openTimelinePanel,
    afterScrollRequested,
    setAfterScrollRequested,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigationContext() {
  return useContext(NavigationContext);
}
